import { Controller } from "@hotwired/stimulus";
import flatpickr from "flatpickr";
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect/index";

export default class extends Controller {
  static targets = ["timePicker", "dateTimePicker", "monthPicker", "datePicker"];
  static values = {
    enableTime: Boolean,
    dateFormat: String,
    noCalendar: Boolean,
    minuteIncrement: Number,
    minDate: String,
  };

  connect() {
    this.initializeTimePickerFlatpickr();
    this.initializeDateTimePickerFlatpickr();
    this.initializeMonthPicker();
    this.initializeDatePicker();
  }

  initializeTimePickerFlatpickr() {
    if (!this.hasTimePickerTarget) return;
    flatpickr(this.timePickerTarget, {
      enableTime: this.enableTimeValue || true,
      noCalendar: this.noCalendarValue || true,
      dateFormat: this.dateFormatValue || "h:i K",
      minuteIncrement: this.minuteIncrementValue || 1,
      minDate: this.minDateValue || "today",
    });
  }

  initializeDateTimePickerFlatpickr() {
    if (!this.hasDateTimePickerTarget) return;
    flatpickr(this.dateTimePickerTarget, {
      enableTime: this.enableTimeValue || true,
      dateFormat: this.dateFormatValue || "F j, Y h:i K",
      minuteIncrement: this.minuteIncrementValue || 1,
      minDate: this.minDateValue || "today",
    });
  }

  // Month-only picker (used by the email marketing calendar). Emits `flatpickr:dateChange`
  // with the selected date so a calendar controller can reload that month.
  initializeMonthPicker() {
    if (!this.hasMonthPickerTarget) return;
    flatpickr(this.monthPickerTarget, {
      plugins: [monthSelectPlugin({ shorthand: false, dateFormat: "F Y", altFormat: "F Y" })],
      // Pass a Date object: a string defaultDate is parsed with `dateFormat` ("F Y"),
      // which can't read the ISO date in data-default-date, leaving the input blank.
      defaultDate: this.parseDataDate(this.monthPickerTarget) || new Date(),
      disableMobile: true,
      onChange: (dates) => this.emitDateChange(dates[0]),
    });
  }

  // Build a local Date from a "YYYY-MM-DD" data-default-date attribute.
  parseDataDate(target) {
    const raw = target.dataset.defaultDate;
    if (!raw) return null;
    const [y, m, d] = raw.split("-").map(Number);
    return new Date(y, m - 1, d);
  }

  // Plain day picker; emits `flatpickr:dateChange` on selection.
  initializeDatePicker() {
    if (!this.hasDatePickerTarget) return;
    flatpickr(this.datePickerTarget, {
      altInput: true,
      altInputClass: "emd-cal-mobile-input",
      altFormat: "D, M j, Y",
      dateFormat: "Y-m-d",
      defaultDate: this.datePickerTarget.dataset.defaultDate || "today",
      disableMobile: true,
      onChange: (dates) => this.emitDateChange(dates[0]),
    });
  }

  emitDateChange(date) {
    if (date) this.dispatch("dateChange", { detail: { date } });
  }

  // Chevrons step the month picker; setDate fires onChange -> emitDateChange.
  prevMonth() {
    this.stepMonth(-1);
  }

  nextMonth() {
    this.stepMonth(1);
  }

  stepMonth(delta) {
    const fp = this.monthPickerTarget._flatpickr;
    const current = fp.selectedDates[0] || new Date();
    fp.setDate(new Date(current.getFullYear(), current.getMonth() + delta, 1), true);
  }

  // Chevrons step the day picker by a week.
  prevWeek() {
    this.stepWeek(-7);
  }

  nextWeek() {
    this.stepWeek(7);
  }

  stepWeek(days) {
    const fp = this.datePickerTarget._flatpickr;
    const current = fp.selectedDates[0] || new Date();
    const next = new Date(current);
    next.setDate(next.getDate() + days);
    fp.setDate(next, true);
  }
}
